import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { AlertCircle, FileText, UserCog, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KYC_LABELS = {
  identity_front: "Pièce d'identité (Recto)", identity_back: "Pièce d'identité (Verso)",
  proof_address: "Justificatif de domicile", iban: "IBAN / RIB",
  proof_income: "Justificatif de revenus", bank_statement: "Relevé bancaire",
  tax_return: "Avis d'imposition", kbis: "KBIS < 3 mois", other: "Autre",
};

export default function PendingActionsWidget({ onNavigateClient }) {
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKyc, setShowKyc] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats/pending-actions`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, [fetchData]);

  const handleKycAction = async (docId, action) => {
    try {
      await axios.put(`${API}/kyc/${docId}`, { status: action === 'approve' ? 'approved' : 'rejected' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === 'approve' ? 'Document approuvé' : 'Document refusé');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleProfileAction = async (clientId, action) => {
    try {
      await axios.put(`${API}/clients/${clientId}/review-changes`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === 'approve' ? 'Modifications approuvées' : 'Modifications refusées');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const [showBenDocs, setShowBenDocs] = useState(false);

  const handleBenDocAction = async (clientId, beneficiaryName, action) => {
    try {
      // Update the beneficiary's id_status in the client profile
      const clientRes = await axios.get(`${API}/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      const beneficiaries = clientRes.data?.beneficiaries || [];
      const updated = beneficiaries.map(b => {
        if (`${b.first_name} ${b.last_name}`.trim() === beneficiaryName) {
          return { ...b, id_status: action === 'approve' ? 'approved' : 'rejected' };
        }
        return b;
      });
      await axios.put(`${API}/clients/${clientId}`, { beneficiaries: updated }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === 'approve' ? `Documents de ${beneficiaryName} validés` : `Documents de ${beneficiaryName} refusés`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading || !data || data.total === 0) return null;

  return (
    <>
      <Card className="border-amber-500/30 bg-amber-500/5" data-testid="pending-actions-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            Actions en attente
            <Badge className="bg-amber-500 text-black ml-auto">{data.total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.kyc_count > 0 && (
            <button onClick={() => setShowKyc(true)} className="w-full flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-secondary/30 transition-colors" data-testid="pending-kyc-btn">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold">Documents KYC</p>
                  <p className="text-xs text-muted-foreground">{data.kyc_count} document{data.kyc_count > 1 ? 's' : ''} en attente de vérification</p>
                </div>
              </div>
              <Badge variant="outline" className="text-blue-500 border-blue-500">{data.kyc_count}</Badge>
            </button>
          )}
          {data.profile_changes_count > 0 && (
            <button onClick={() => setShowChanges(true)} className="w-full flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-secondary/30 transition-colors" data-testid="pending-changes-btn">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <UserCog className="w-4 h-4 text-purple-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold">Modifications profil</p>
                  <p className="text-xs text-muted-foreground">{data.profile_changes_count} demande{data.profile_changes_count > 1 ? 's' : ''} de modification</p>
                </div>
              </div>
              <Badge variant="outline" className="text-purple-500 border-purple-500">{data.profile_changes_count}</Badge>
            </button>
          )}
          {data.beneficiary_docs_count > 0 && (
            <button onClick={() => setShowBenDocs(true)} className="w-full flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-secondary/30 transition-colors" data-testid="pending-bendocs-btn">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-cyan-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold">Documents bénéficiaires</p>
                  <p className="text-xs text-muted-foreground">{data.beneficiary_docs_count} pièce{data.beneficiary_docs_count > 1 ? 's' : ''} d'identité en attente</p>
                </div>
              </div>
              <Badge variant="outline" className="text-cyan-500 border-cyan-500">{data.beneficiary_docs_count}</Badge>
            </button>
          )}
        </CardContent>
      </Card>

      {/* KYC Pending Dialog */}
      <Dialog open={showKyc} onOpenChange={setShowKyc}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Documents KYC en attente ({data.kyc_count})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {data.kyc_pending.map(doc => (
              <div key={doc.id} className="p-3 rounded-lg border bg-card flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{doc.client_name || 'Client'}</p>
                  <p className="text-xs text-muted-foreground">{doc.client_email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px]">{KYC_LABELS[doc.document_type] || doc.label || doc.document_type}</Badge>
                    <span className="text-[10px] text-muted-foreground">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : ''}</span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {onNavigateClient && (
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setShowKyc(false); onNavigateClient(doc.client_id); }}>
                      <Eye className="w-3 h-3" />
                    </Button>
                  )}
                  <Button size="sm" className="h-7 px-2 bg-green-600 hover:bg-green-700" onClick={() => handleKycAction(doc.id, 'approve')}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Valider
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-red-500 hover:bg-red-500/10" onClick={() => handleKycAction(doc.id, 'reject')}>
                    <XCircle className="w-3 h-3 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Changes Dialog */}
      <Dialog open={showChanges} onOpenChange={setShowChanges}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-500" />
              Modifications profil en attente ({data.profile_changes_count})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {data.profile_changes.map((pc, idx) => (
              <div key={idx} className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-sm">{pc.client_name}</p>
                    <p className="text-xs text-muted-foreground">{pc.client_email} — {pc.submitted_at ? new Date(pc.submitted_at).toLocaleDateString('fr-FR') : ''}</p>
                  </div>
                  <div className="flex gap-1.5">
                    {onNavigateClient && (
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setShowChanges(false); onNavigateClient(pc.client_id); }}>
                        <Eye className="w-3 h-3" />
                      </Button>
                    )}
                    <Button size="sm" className="h-7 px-2 bg-green-600 hover:bg-green-700" onClick={() => handleProfileAction(pc.client_id, 'approve')}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approuver
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-red-500 hover:bg-red-500/10" onClick={() => handleProfileAction(pc.client_id, 'reject')}>
                      <XCircle className="w-3 h-3 mr-1" /> Refuser
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {Object.entries(pc.changes || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-28 flex-shrink-0">{key.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-primary">{typeof val === 'string' ? val : JSON.stringify(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Beneficiary Documents Dialog */}
      <Dialog open={showBenDocs} onOpenChange={setShowBenDocs}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-500" />
              Documents bénéficiaires en attente ({data.beneficiary_docs_count})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {data.beneficiary_docs.map((bd, idx) => (
              <div key={idx} className="p-4 rounded-lg border bg-card space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{bd.client_name}</p>
                    <p className="text-xs text-muted-foreground">{bd.client_email}</p>
                  </div>
                  <Badge variant="outline" className="text-cyan-500">{bd.id_type === 'passport' ? 'Passeport' : 'Carte d\'identité'}</Badge>
                </div>
                <div className="p-3 rounded bg-muted/30">
                  <p className="text-sm font-semibold mb-1">Bénéficiaire : {bd.beneficiary_name}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    {bd.relation && <span>Relation : {bd.relation}</span>}
                    {bd.percentage > 0 && <span>{bd.percentage}%</span>}
                    {bd.phone && <span>Tél : {bd.phone}</span>}
                  </div>
                </div>
                {/* Document previews */}
                <div className="flex gap-3">
                  {bd.id_front_url && (
                    <a href={bd.id_front_url} target="_blank" rel="noreferrer" className="flex-1 p-2 rounded border text-center hover:bg-secondary/30 transition-colors">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                      <p className="text-[10px] font-semibold">Recto</p>
                    </a>
                  )}
                  {bd.id_back_url && (
                    <a href={bd.id_back_url} target="_blank" rel="noreferrer" className="flex-1 p-2 rounded border text-center hover:bg-secondary/30 transition-colors">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                      <p className="text-[10px] font-semibold">Verso</p>
                    </a>
                  )}
                  {bd.passport_url && (
                    <a href={bd.passport_url} target="_blank" rel="noreferrer" className="flex-1 p-2 rounded border text-center hover:bg-secondary/30 transition-colors">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                      <p className="text-[10px] font-semibold">Passeport</p>
                    </a>
                  )}
                </div>
                {/* Actions */}
                <div className="flex gap-2 justify-end">
                  {onNavigateClient && (
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setShowBenDocs(false); onNavigateClient(bd.client_id); }}>
                      <Eye className="w-3 h-3 mr-1" /> Fiche client
                    </Button>
                  )}
                  <Button size="sm" className="h-7 px-3 bg-green-600 hover:bg-green-700" onClick={() => handleBenDocAction(bd.client_id, bd.beneficiary_name, 'approve')}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Valider
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-3 text-red-500 hover:bg-red-500/10" onClick={() => handleBenDocAction(bd.client_id, bd.beneficiary_name, 'reject')}>
                    <XCircle className="w-3 h-3 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
